import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Swal from "sweetalert2";
import { deleteDoc, onSnapshot } from "firebase/firestore";
import Nav from "./Nav";
import Sidebar from "./Sidebar";
import Breadcrumb from "./Breadcrumb";
import Spinner from "./Spinner";
import { addDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import NotificationsPortal, { notify } from "../components/NotificationsPortal";
import { useNavContext } from "../context/NavContext";
import { useAuth } from "../context/AuthContext";
import { orgCollection, orgDocIn } from "../utils/firePaths";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";
import TableToolbar from "./TableToolbar";
import DataTable from "./DataTable";
import TablePagination from "./TablePagination";
import ModalNuevaSucursal from "../components/ModalNuevaSucursal";
import SucursalDetailModal from "../components/SucursalDetailModal";

const toStr = (v, fb = "") => (v === undefined || v === null ? fb : String(v));

const normalizeEstado = (s) => {
  if (typeof s === "boolean") return s ? "Activo" : "Inactivo";
  const raw = toStr(s, "").trim();
  if (!raw) return "Activo";
  const low = raw.toLowerCase();
  if (["activo", "activa", "enabled", "true", "1"].includes(low))
    return "Activo";
  if (["inactivo", "inactiva", "disabled", "false", "0"].includes(low))
    return "Inactivo";
  return raw;
};

const normalizeSucursal = (row) => {
  const codigo =
    row.codigo ||
    row.clave ||
    row.key ||
    row.cod ||
    row.code ||
    row.numero ||
    "";

  const nombre = row.nombre || row.almacenNombre || row.sucursalNombre || "";

  const tipo = row.tipo || row.tipoSucursal || row.clasificacion || "";

  const direccion =
    row.direccion ||
    row.domicilio ||
    row.address ||
    row.ubicacion ||
    row.direccionCompleta ||
    "";

  const responsable =
    row.responsable ||
    row.encargado ||
    row.contacto ||
    row.nombreResponsable ||
    "";

  const telefono = row.telefono || row.tel || row.phone || "";
  const correo = row.correo || row.email || row.mail || "";

  const fechaCreacion =
    row.fechaCreacion ||
    row.createdAt ||
    row.created_at ||
    row.createdOn ||
    row.fechaAlta ||
    "";

  const estado = normalizeEstado(row.estado ?? row.activo ?? row.isActive);

  return {
    ...row,
    codigo: toStr(codigo),
    nombre: toStr(nombre),
    tipo: toStr(tipo),
    direccion: toStr(direccion),

    calle: toStr(row.calle),
    numExterior: toStr(row.numExterior),
    numInterior: toStr(row.numInterior),
    cp: toStr(row.cp),
    colonia: toStr(row.colonia),
    municipio: toStr(row.municipio),
    estadoDireccion: toStr(row.estadoDireccion),
    pais: toStr(row.pais),

    responsable: toStr(responsable),
    telefono: toStr(telefono),
    correo: toStr(correo),
    fechaCreacion,
    estado: toStr(estado),
  };
};

const badgeTipoClass = (tipo) => {
  const t = (tipo || "").toLowerCase();
  if (t.includes("principal")) return "badge-pill badge-blue";
  return "badge-pill badge-gray";
};

const badgeEstadoClass = (estado) => {
  const e = (estado || "").toLowerCase();
  if (e.includes("activo")) return "badge-pill badge-green";
  return "badge-pill badge-gray";
};

const Sucursales = () => {
  const { orgId } = useAuth();
  useFunctionsPrevia();

  const { searchTerm, setSearchTerm, proximasAVencer, proximosEventos } =
    useNavContext();

  const [loading, setLoading] = useState(true);
  const [sucursales, setSucursales] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterStatus, setFilterStatus] = useState("Todos");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [openNuevaSucursal, setOpenNuevaSucursal] = useState(false);
  const [openSucursalDetail, setOpenSucursalDetail] = useState(false);
  const [selectedSucursal, setSelectedSucursal] = useState(null);
  const [sucursalParaEditar, setSucursalParaEditar] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const defaults = {
      Codigo: true,
      Nombre: true,
      Tipo: true,
      Direccion: true,
      Responsable: true,
      Telefono: true,
      Correo: false,
      Estado: true,
      FechaCreacion: false,
    };

    const saved = localStorage.getItem("columnPrefs_sucursales");
    if (!saved) return defaults;

    try {
      const parsed = JSON.parse(saved);
      return { ...defaults, ...parsed };
    } catch {
      return defaults;
    }
  });

  useEffect(() => {
    localStorage.setItem(
      "columnPrefs_sucursales",
      JSON.stringify(visibleColumns),
    );
  }, [visibleColumns]);

  const [sortConfig, setSortConfig] = useState({
    key: "codigo",
    direction: "asc",
  });

  const fileInputRef = useRef(null);

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => {
      const direction =
        prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    if (!orgId) return;

    setLoading(true);

    const ref = orgCollection(orgId, "sucursales");
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        const updated = snapshot.docs.map((d) =>
          normalizeSucursal({ id: d.id, ...d.data() }),
        );
        setSucursales(updated);
        setLoading(false);
      },
      (error) => {
        console.error("onSnapshot sucursales error:", error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [orgId]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(sucursales)) return [];

    const searchLower = (searchTerm || "").toLowerCase().trim();

    return sucursales.filter((s) => {
      const estado = normalizeEstado(s.estado);

      const matchStatus = filterStatus === "Todos" || estado === filterStatus;
      if (!matchStatus) return false;

      if (!searchLower) return true;

      const fields = [
        s.codigo,
        s.nombre,
        s.tipo,
        s.direccion,
        s.responsable,
        s.telefono,
        s.correo,
        estado,
      ]
        .map((x) => toStr(x).toLowerCase())
        .filter(Boolean);

      const words = searchLower.split(/\s+/);
      return words.every((w) => fields.some((f) => f.includes(w)));
    });
  }, [sucursales, searchTerm, filterStatus]);

  const sortedData = useMemo(() => {
    const { key, direction } = sortConfig || {};
    const dir = direction === "desc" ? -1 : 1;

    const getVal = (row) => {
      if (key === "estado") return normalizeEstado(row.estado);
      return row?.[key];
    };

    return [...filteredData].sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);

      if (key === "codigo") {
        const na = Number(String(va || "").replace(/\D/g, "")) || 0;
        const nb = Number(String(vb || "").replace(/\D/g, "")) || 0;

        if (na && nb) return (na - nb) * dir;

        const sa = toStr(va).toLowerCase();
        const sb = toStr(vb).toLowerCase();
        return sa.localeCompare(sb, "es") * dir;
      }

      const sa = toStr(va).toLowerCase();
      const sb = toStr(vb).toLowerCase();
      return sa.localeCompare(sb, "es") * dir;
    });
  }, [filteredData, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / itemsPerPage));

  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return sortedData.slice(start, end);
  }, [sortedData, currentPage, itemsPerPage]);

  const toggleSelectAll = useCallback(() => {
    const pageIds = currentItems.map((i) => i.id);
    const allSelected =
      pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  }, [currentItems, selectedIds]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showColumnConfig &&
        !e.target.closest(".column-config") &&
        !e.target.closest('[data-dd="columns"]')
      ) {
        setShowColumnConfig(false);
      }
      if (
        showExportDropdown &&
        !e.target.closest(".export-menu") &&
        !e.target.closest('[data-dd="export"]')
      ) {
        setShowExportDropdown(false);
      }
      if (
        showFilterDropdown &&
        !e.target.closest(".filter-panel") &&
        !e.target.closest('[data-dd="filter"]')
      ) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showColumnConfig, showExportDropdown, showFilterDropdown]);

  const handleEditSelected = useCallback(() => {
    if (selectedIds.length !== 1) return;

    const selectedId = selectedIds[0];
    const sucursal = sucursales.find((s) => s.id === selectedId);

    if (!sucursal) return;

    setSucursalParaEditar(sucursal);
    setOpenSucursalDetail(false);
    setSelectedSucursal(null);
    setOpenNuevaSucursal(true);
  }, [selectedIds, sucursales]);

  const handleDeleteSelected = useCallback(async () => {
    if (!orgId) {
      Swal.fire({
        title: "Error",
        text: "No se detectó orgId. Intenta recargar la página o seleccionar tu organización.",
        icon: "error",
        customClass: { popup: "swal-popup-reset" },
      });
      return;
    }

    if (selectedIds.length === 0) return;

    const count = selectedIds.length;
    const esSingular = count === 1;
    const texto = esSingular ? "sucursal" : "sucursales";
    const titulo = esSingular ? "Eliminar Sucursal" : "Eliminar Sucursales";

    const result = await Swal.fire({
      html: `
        <div class="custom-modal-container">
          <div class="custom-modal-header">
            <div class="custom-modal-icon-box">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" x2="12" y1="8" y2="12"></line>
                <line x1="12" x2="12.01" y1="16" y2="16"></line>
              </svg>
            </div>
            <h3 class="custom-modal-title">${titulo}</h3>
          </div>
          <p class="custom-modal-description">¿Estás seguro de que deseas eliminar ${count} ${texto}?</p>
          <div class="custom-modal-actions">
            <button id="cancel-btn" class="btn-modal-cancel">Cancelar</button>
            <button id="confirm-btn" class="btn-modal-delete">Eliminar</button>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: false,
      buttonsStyling: false,
      allowOutsideClick: false,
      customClass: {
        popup: "swal-popup-reset",
        htmlContainer: "swal-html-reset",
      },
      didOpen: () => {
        document
          .getElementById("cancel-btn")
          .addEventListener("click", () => Swal.close());
        document
          .getElementById("confirm-btn")
          .addEventListener("click", () => Swal.clickConfirm());
      },
    });

    if (!result.isConfirmed) return;

    try {
      await Promise.all(
        selectedIds.map(async (id) => {
          const ref = orgDocIn(orgId, "sucursales", id);
          await deleteDoc(ref);
        }),
      );

      setSelectedIds([]);

      notify({
        type: "success",
        message:
          count === 1
            ? "1 sucursal eliminada exitosamente"
            : `${count} sucursal(es) eliminada(s) exitosamente`,
        duration: 3500,
      });
    } catch (error) {
      console.error("Error al eliminar sucursales:", error);
      notify({
        type: "error",
        message: `No se pudo eliminar: ${error.message}`,
        duration: 4500,
      });
    }
  }, [orgId, selectedIds]);

  const columnsSucursales = useMemo(() => {
    return [
      {
        id: "codigo",
        label: "Código",
        sortKey: "codigo",
        visible: visibleColumns.Codigo,
        cellClassName: "table-td col-center",
        render: (row) => row.codigo || "---",
      },
      {
        id: "nombre",
        label: "Nombre",
        sortKey: "nombre",
        visible: visibleColumns.Nombre,
        cellClassName: "table-td col-left",
        render: (row) => (
          <span style={{ fontWeight: 400, color: "#111827" }}>
            {row.nombre || "---"}
          </span>
        ),
      },
      {
        id: "tipo",
        label: "Tipo",
        sortKey: "tipo",
        visible: visibleColumns.Tipo,
        cellClassName: "table-td col-center",
        render: (row) => (
          <span className={badgeTipoClass(row.tipo)}>{row.tipo || "—"}</span>
        ),
      },
      {
        id: "direccion",
        label: "Dirección",
        sortKey: "direccion",
        visible: visibleColumns.Direccion,
        cellClassName: "table-td col-left",
        render: (row) => (
          <div style={{ minWidth: 0, width: "100%" }}>
            <span
              className="ellipsis"
              title={row.direccion || ""}
              style={{ color: "#475569", fontWeight: 400 }}
            >
              {row.direccion || "---"}
            </span>
          </div>
        ),
      },
      {
        id: "responsable",
        label: "Responsable",
        sortKey: "responsable",
        visible: visibleColumns.Responsable,
        cellClassName: "table-td col-left",
        render: (row) => (
          <span style={{ color: "#334155", fontWeight: 400 }}>
            {row.responsable || "---"}
          </span>
        ),
      },
      {
        id: "telefono",
        label: "Teléfono",
        sortKey: "telefono",
        visible: visibleColumns.Telefono,
        cellClassName: "table-td col-center",
        render: (row) => (
          <span style={{ color: "#475569", fontWeight: 400 }}>
            {row.telefono || "---"}
          </span>
        ),
      },
      {
        id: "correo",
        label: "Correo",
        sortKey: "correo",
        visible: visibleColumns.Correo,
        cellClassName: "table-td col-left",
        render: (row) => (
          <span style={{ color: "#475569", fontWeight: 400 }}>
            {row.correo || "---"}
          </span>
        ),
      },
      {
        id: "estado",
        label: "Estado",
        sortKey: "estado",
        visible: visibleColumns.Estado,
        cellClassName: "table-td col-center",
        render: (row) => (
          <span className={badgeEstadoClass(row.estado)}>
            {normalizeEstado(row.estado)}
          </span>
        ),
      },
      {
        id: "fechaCreacion",
        label: "Fecha de Creación",
        sortKey: "fechaCreacion",
        visible: visibleColumns.FechaCreacion,
        cellClassName: "table-td col-center",
        render: (row) => {
          const d =
            row.fechaCreacion?.toDate?.() ||
            (row.fechaCreacion ? new Date(row.fechaCreacion) : null);
          if (!d || isNaN(d.getTime())) return "---";
          return d.toLocaleDateString("es-MX", {
            year: "numeric",
            month: "short",
            day: "2-digit",
          });
        },
      },
    ];
  }, [visibleColumns]);

  return (
    <>
      <NotificationsPortal />

      <div className="no-sticky">
        <Nav
          proximasAVencer={proximasAVencer}
          proximosEventos={proximosEventos}
        />
        <Sidebar />

        <style>{`
          .badge-pill{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            padding:6px 12px;
            border-radius:999px;
            font-size:13px;
            font-weight:500;
            border:1px solid transparent;
            line-height:1;
            white-space:nowrap;
          }
          .badge-blue{
            background:#eff6ff;
            border-color:#bfdbfe;
            color:#2563eb;
          }
          .badge-gray{
            background:#f3f4f6;
            border-color:#e5e7eb;
            color:#374151;
          }
          .badge-green{
            background:#dcfce7;
            border-color:#bbf7d0;
            color:#16a34a;
          }
          .ellipsis{
            display: block;      
            width: 100%;          
            min-width: 0;          
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
           }
        `}</style>

        <div className="main-content-wrapper">
          <div style={{ marginBottom: "25px" }}>
            <Breadcrumb />
            <h1 className="page-title">Sucursales</h1>
          </div>

          <TableToolbar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchPlaceholder="Buscar sucursales..."
            showFilterDropdown={showFilterDropdown}
            setShowFilterDropdown={setShowFilterDropdown}
            showColumnConfig={showColumnConfig}
            setShowColumnConfig={setShowColumnConfig}
            showExportDropdown={showExportDropdown}
            setShowExportDropdown={setShowExportDropdown}
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
            filterStatus={filterStatus}
            setFilterStatus={(v) => {
              setFilterStatus(v);
              setCurrentPage(1);
            }}
            filterOptions={["Todos", "Activo", "Inactivo"]}
            onAdd={() => {
              setSucursalParaEditar(null);
              setOpenNuevaSucursal(true);
            }}
            addButtonTooltip="Agregar Sucursal"
            fileInputRef={fileInputRef}
            onExport={() => console.log("Exportando sucursales...")}
          />

          {selectedIds.length > 0 && (
            <div className="selection-bar">
              <span>
                {selectedIds.length === 1
                  ? "1 seleccionado"
                  : `${selectedIds.length} seleccionado(s)`}
              </span>

              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="btn-selection btn-edit"
                  style={{ opacity: selectedIds.length > 1 ? 0.5 : 1 }}
                  disabled={selectedIds.length > 1}
                  onClick={handleEditSelected}
                >
                  <i className="bi bi-pencil-square" /> <span>Editar</span>
                </button>

                <button
                  className="btn-selection btn-delete"
                  onClick={handleDeleteSelected}
                >
                  <i className="bi bi-trash" /> <span>Eliminar</span>
                </button>
              </div>
            </div>
          )}

          <div className="card-table">
            <DataTable
              loading={loading}
              loadingFallback={
                <div style={{ padding: "50px" }}>
                  <Spinner />
                </div>
              }
              rows={currentItems}
              columns={columnsSucursales}
              selectedIds={selectedIds}
              onToggleAll={toggleSelectAll}
              onToggleRow={(id) =>
                setSelectedIds((prev) =>
                  prev.includes(id)
                    ? prev.filter((x) => x !== id)
                    : [...prev, id],
                )
              }
              sortConfig={sortConfig}
              onSort={handleSort}
              onRowClick={(row) => {
                setSucursalParaEditar(null);
                setSelectedSucursal(row);
                setOpenSucursalDetail(true);
              }}
              emptyTitle="Aún no hay sucursales."
              emptySubtitle="Crea una sucursal nueva o importa un archivo para comenzar."
              emptyIcon={
                <img
                  src="/img/no-data.svg"
                  alt="Sin datos"
                  style={{ width: 220, maxWidth: "70%", opacity: 0.95 }}
                />
              }
              footer={
                <TablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(p) => setCurrentPage(p)}
                  itemsPerPage={itemsPerPage}
                  onItemsPerPageChange={(n) => {
                    setItemsPerPage(n);
                    setCurrentPage(1);
                  }}
                  itemsPerPageOptions={[10, 25, 50]}
                  selectedCount={selectedIds.length}
                  selectedLabelSingular="sucursal"
                  selectedLabelPlural="sucursales"
                  itemsLabel="Mostrando:"
                />
              }
            />
            <ModalNuevaSucursal
              isOpen={openNuevaSucursal}
              onClose={() => {
                setOpenNuevaSucursal(false);
                setSucursalParaEditar(null);
              }}
              initialData={sucursalParaEditar}
              onSubmit={async (data) => {
                if (!orgId) {
                  notify({
                    type: "error",
                    message: "No se detectó orgId.",
                    duration: 3500,
                  });
                  return;
                }

                if (sucursalParaEditar?.id) {
                  const ref = orgDocIn(
                    orgId,
                    "sucursales",
                    sucursalParaEditar.id,
                  );

                  await updateDoc(ref, {
                    ...data,
                  });

                  notify({
                    type: "success",
                    message: "Sucursal actualizada correctamente.",
                    duration: 3000,
                  });
                } else {
                  await addDoc(orgCollection(orgId, "sucursales"), {
                    ...data,
                    estado: "Activo",
                    fechaCreacion: serverTimestamp(),
                  });

                  notify({
                    type: "success",
                    message: "Sucursal creada correctamente.",
                    duration: 3000,
                  });
                }

                setSucursalParaEditar(null);
                setSelectedIds([]);
              }}
            />
            <SucursalDetailModal
              isOpen={openSucursalDetail}
              onClose={() => {
                setOpenSucursalDetail(false);
                setSelectedSucursal(null);
              }}
              sucursal={selectedSucursal}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Sucursales;
